<template>
    <div>
        <div
            class="u-wrapper text-gray-medium bg-light d-flex rounded-sm position-relative"
            :class="[
                !disabled && !readonly
                    ? 'cursor--pointer'
                    : 'cursor--not-allowed',
                focused && 'is-focused',
                focused && $css($theme.borderPrimary),
                borderStatus,
            ]"
        >
            <div
                data-t="spinner-down"
                class="p-12 u-spinner text-gray-dark"
                @click="down"
            >
                <v-icon
                    name="minus"
                    :w="20"
                />
            </div>

            <input
                ref="input"
                type="number"
                class="p-12"
                :class="[borderStatus, $css($theme.formControlPrimary)]"
                :autocomplete="autocomplete"
                :autofocus="autofocus"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
                :value="value"
                :min="min"
                :max="max"
                :step="step"
                @input="input"
                @focus="focus"
                @blur="blur"
                @click="$emit('click')"
            />

            <div
                data-t="spinner-up"
                class="p-12 u-spinner text-gray-dark"
                @click="up"
            >
                <v-icon
                    name="plus"
                    :w="20"
                />
            </div>
        </div>

        <input-status :status="status" />
    </div>
</template>

<script>
import InputStatus from "~/components/inputs/InputStatus"

export default {
    props: {
        ...InputStatus.props,

        autocomplete: {
            type: String,
            default: "off",
            validator: val => ["on", "off"].includes(val),
        },

        autofocus: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        max: {
            type: Number,
            default: Infinity,
        },

        min: {
            type: Number,
            default: -Infinity,
        },

        placeholder: {
            type: String,
            default: "",
        },

        readonly: {
            type: Boolean,
            default: false,
        },

        step: {
            type: Number,
            default: 1,
        },

        value: {
            type: Number,
            default: null,
        },
    },

    emits: ["update:value", "click", "focus", "blur"],

    data() {
        return {
            focused: false,
        }
    },

    computed: {
        borderStatus() {
            return this.status.code ? `border-${this.status.code}` : ""
        },
    },

    methods: {
        input(event) {
            const newValue = parseInt(event.target.value.trim(), 10)
            if (Number.isNaN(newValue)) return
            this.$emit("update:value", newValue)
        },

        down() {
            if (this.disabled || this.readonly) return

            // Prevents values going out of bounds when the user's input
            // comes from a mix of keystrokes and clicks
            let newValue = Math.max(this.value - this.step, this.min)
            this.$emit("update:value", newValue)
            this.$refs.input.focus()
        },

        up() {
            if (this.disabled || this.readonly) return
            // Prevents values going out of bounds when the user's input
            // comes from a mix of keystrokes and clicks
            let newValue = Math.min(this.value + this.step, this.max)
            this.$emit("update:value", newValue)
            this.$refs.input.focus()
        },

        focus() {
            this.focused = true
            this.$emit("focus")
        },

        blur() {
            this.focused = false
            this.$emit("blur")
        },
    },

    components: {
        InputStatus,
    },
}
</script>

<style lang="scss" scoped>
@import "~/scss/bootstrap-importable";

.u-wrapper {
    border: 1px solid $gray-medium;

    &:hover {
        border-color: $gray-dark;
    }

    &.is-focused .u-spinner {
        color: $dark;
    }
}

input {
    border: solid $gray-medium;
    border-width: 0 1px;
    color: $gray-dark;
    text-align: center;
    outline: none;
    min-width: 58px;
    width: 100%;

    &::placeholder {
        color: $gray-dark;
    }

    &:focus {
        color: $dark;
    }

    &:disabled {
        background: $light;
        opacity: 0.5;
    }
}

// RESET BROWSER DEFAULTS
// We ain't going to use browser default spinners
input {
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
    }
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
